import { r, s } from '@/theme/tokens';
import { Box, Checkbox, Chip, Text } from '@/ui/primitives';
import { tableCellTagChipSx } from '../table/cells/sharedCellContent';
import { TagsFilterFieldEmptyOption } from './TagsFilterFieldEmptyOption';
import type { TagOption } from './TagsFilterFieldTypes';
import type { HTMLAttributes, Key, ReactElement, ReactNode } from 'react';

type TagsFilterFieldOptionProps = {
  emptyOptionsAction?: ReactNode;
  emptyOptionValue: string;
  hasCountColumn: boolean;
  multiple: boolean;
  option: TagOption;
  optionCheckboxSlotWidth: string;
  optionFontSizePx: number;
  optionKey: Key;
  optionProps: HTMLAttributes<HTMLLIElement>;
  optionRowHeight: string;
  renderOptionLabel?: (option: TagOption) => ReactNode;
  selected: boolean;
  showOptionChips: boolean;
  actionOptionValue: string;
  onActionClick: () => void;
};

export const TagsFilterFieldOption = ({
  emptyOptionsAction,
  emptyOptionValue,
  hasCountColumn,
  multiple,
  option,
  optionCheckboxSlotWidth,
  optionFontSizePx,
  optionKey,
  optionProps,
  optionRowHeight,
  renderOptionLabel,
  selected,
  showOptionChips,
  actionOptionValue,
  onActionClick,
}: TagsFilterFieldOptionProps): ReactElement => {
  const isEmptyOption = option.value === emptyOptionValue;
  const isActionOption = option.value === actionOptionValue;
  const CountIcon = option.countIcon;
  const shouldRenderLeadingIcon =
    CountIcon !== undefined && !showOptionChips && !isEmptyOption && !isActionOption;

  return (
    <Box
      component="li"
      key={optionKey}
      {...optionProps}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      sx={{
        display: hasCountColumn ? 'grid' : 'flex',
        gridTemplateColumns: hasCountColumn
          ? `${optionCheckboxSlotWidth} minmax(0, 1fr) max-content`
          : undefined,
        alignItems: 'center',
        gap: showOptionChips ? s('md') : s('sm'),
        px: s('none'),
        py: s('none'),
        width: hasCountColumn ? '100%' : undefined,
        height: optionRowHeight,
        minHeight: optionRowHeight,
        maxHeight: optionRowHeight,
        minWidth: 0,
      }}
    >
      {isEmptyOption || isActionOption || !multiple ? null : (
        <Checkbox
          size="small"
          checked={selected}
          sx={{
            p: s('none'),
            m: s('none'),
            mr: s('none'),
            '& .MuiSvgIcon-root': { fontSize: '1rem' },
          }}
        />
      )}
      {isActionOption ? (
        <TagsFilterFieldEmptyOption
          action={emptyOptionsAction}
          actionPlacement="left"
          optionFontSizePx={optionFontSizePx}
          onActionClick={onActionClick}
        />
      ) : isEmptyOption ? (
        <TagsFilterFieldEmptyOption
          label={option.label}
          optionFontSizePx={optionFontSizePx}
          onActionClick={onActionClick}
        />
      ) : showOptionChips ? (
        <Chip
          size="xs"
          tone={option.color ?? 'neutral'}
          label={option.label}
          sx={[
            tableCellTagChipSx,
            {
              borderRadius: r('sm'),
              height: s('2xl'),
              justifySelf: 'flex-start',
              maxWidth: '100%',
              minWidth: 0,
              width: 'fit-content',
              '& .MuiChip-label': {
                minWidth: 0,
                overflow: 'hidden',
                py: s('xxs'),
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              '& .MuiChip-label .MuiTypography-root': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            },
          ]}
        />
      ) : (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            flex: '1 1 auto',
            gap: CountIcon ? `calc((${s('sm')} + ${s('md')}) / 2)` : s('none'),
            minWidth: 0,
            pl: CountIcon ? s('xs') : s('none'),
            width: '100%',
          }}
        >
          {shouldRenderLeadingIcon ? (
            <CountIcon
              aria-hidden="true"
              fontSize="small"
              sx={{
                width: '1em',
                height: '1em',
                flex: '0 0 auto',
                color: option.countIconColor ?? 'text.secondary',
              }}
            />
          ) : null}
          {renderOptionLabel ? (
            renderOptionLabel(option)
          ) : (
            <Text
              role="bodyMuted"
              color="textPrimary"
              component="span"
              sx={{
                fontSize: `${optionFontSizePx}px`,
                lineHeight: 1.4,
                fontWeight: (theme) => theme.typography.body.fontWeight,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {option.label}
            </Text>
          )}
        </Box>
      )}
      {option.count !== undefined && !isEmptyOption ? (
        <Box
          component="span"
          sx={{
            ml: hasCountColumn ? s('none') : 'auto',
            pl: hasCountColumn ? s('none') : s('lg'),
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: s('xs'),
            minWidth: '3ch',
          }}
        >
          <Text
            role="bodyMuted"
            color="textSecondary"
            component="span"
            sx={{
              fontSize: `${optionFontSizePx}px`,
              lineHeight: 1.4,
              fontWeight: (theme) => theme.typography.fontWeightRegular,
              fontVariantNumeric: 'tabular-nums',
              minWidth: '3ch',
              textAlign: 'right',
            }}
          >
            {option.count.toLocaleString()}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};
